import { useEffect, useRef } from "react";
import api from "@/lib/api";

// Per-session de-dupe so the same ad_id only fires one impression per tab.
const fired = new Set();

/**
 * Fires `POST /api/ads/{ad_id}/impression` exactly once per tab session
 * whenever a real ad (with a non-sample ad_id) is mounted.
 *
 * Sample / fallback ads are no longer served by the backend, but we still
 * guard against `null` / `undefined` and any ad_id that starts with the
 * legacy `sample-` / `inline-` prefix.
 */
export function useAdImpression(ad_id) {
  useEffect(() => {
    if (!ad_id) return;
    if (typeof ad_id !== "string") return;
    if (ad_id.startsWith("sample-") || ad_id.startsWith("inline-")) return;
    if (fired.has(ad_id)) return;
    fired.add(ad_id);
    api.post(`/ads/${ad_id}/impression`).catch(() => {
      // silently swallow — impression tracking is best-effort
      fired.delete(ad_id); // allow retry later if backend was briefly down
    });
  }, [ad_id]);
}

/**
 * Click tracker — call when the user activates an ad (anchor click etc.).
 * Returns a promise so callers can `await` if they need to (we don't block
 * the navigation; the browser follows the link in parallel).
 */
export function trackAdClick(ad_id) {
  if (!ad_id || typeof ad_id !== "string") return Promise.resolve();
  if (ad_id.startsWith("sample-") || ad_id.startsWith("inline-")) return Promise.resolve();
  return api.post(`/ads/${ad_id}/click`).catch(() => {});
}

// Memoised ref used by components that want a stable click handler.
export function useAdClickHandler(ad_id) {
  const ref = useRef(ad_id);
  ref.current = ad_id;
  return () => trackAdClick(ref.current);
}
